// src/components/CalendarScreen.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { collection, query, where, Timestamp, getDocs, onSnapshot, orderBy, collectionGroup } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from '../../firebaseConfig';
import { useAuthUser } from '../../hooks/useAuthUser';
import { useToast } from '../shared/contexts/ToastContext';
import { useConfirmDialog } from '../shared/contexts/ConfirmContext';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { format, startOfMonth, endOfMonth, startOfDay, isEqual } from 'date-fns';
import { FiLoader, FiCalendar, FiFilter, FiX, FiCheckCircle, FiClock, FiAlertCircle, FiSlash, FiExternalLink, FiArrowRight, FiEdit3 } from 'react-icons/fi';

// Helper functions
const getDateString = (date) => format(date, 'yyyy-MM-dd');

const getCalendarDisplayDate = (post) => {
    if (post.status === 'posted' && post.postedAt?.toDate) return post.postedAt.toDate();
    if (post.scheduledTime?.toDate) return post.scheduledTime.toDate();
    return post.createdAt?.toDate();
};

const cancelPostFn = httpsCallable(functions, 'cancelPost');

function CalendarScreen() {
    const { userId } = useAuthUser();
    const { addToast } = useToast();
    const { confirm } = useConfirmDialog();
    const [searchParams] = useSearchParams();

    // State
    const [activeStartDate, setActiveStartDate] = useState(startOfMonth(new Date()));
    const [monthlyPosts, setMonthlyPosts] = useState([]);
    const [projects, setProjects] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [projectLookup, setProjectLookup] = useState({});
    const [campaignLookup, setCampaignLookup] = useState({});

    // Filters — pre-select project from URL query param if present
    const [selectedProjectId, setSelectedProjectId] = useState(searchParams.get('project') || 'all');
    const [selectedCampaignId, setSelectedCampaignId] = useState('all');
    const [selectedStatuses, setSelectedStatuses] = useState(['scheduled', 'posted', 'failed', 'draft']);
    
    // Loading States
    const [loadingPosts, setLoadingPosts] = useState(true);
    const [loadingProjects, setLoadingProjects] = useState(true);
    const [loadingCampaigns, setLoadingCampaigns] = useState(false);
    const [error, setError] = useState('');
    
    // Modals & Selection
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [selectedPostForModal, setSelectedPostForModal] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);

    // --- 1. Fetch Projects ---
    useEffect(() => {
        if (!userId) { setLoadingProjects(false); return; }
        setLoadingProjects(true);
        const projectsRef = collection(db, "projects");
        const q = query(projectsRef, where("userId", "==", userId), orderBy("createdAt", "desc"));
        
        getDocs(q).then((querySnapshot) => {
            const fetchedProjects = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            const projLookup = fetchedProjects.reduce((acc, proj) => {
                acc[proj.id] = proj.projectName || `Project ${proj.id.slice(-4)}`;
                return acc;
            }, {});
            setProjects(fetchedProjects);
            setProjectLookup(projLookup);
        }).catch((err) => {
            setError("Failed to load projects.");
        }).finally(() => setLoadingProjects(false));
    }, [userId]);

    // --- 2. Fetch Campaigns ---
    useEffect(() => {
        if (!userId || selectedProjectId === 'all') {
            setCampaigns([]); setCampaignLookup({}); setSelectedCampaignId('all'); return;
        }
        setLoadingCampaigns(true);
        const streamsRef = collection(db, "projects", selectedProjectId, "contentStreams");
        const q = query(streamsRef, where("userId", "==", userId));
        
        getDocs(q).then((querySnapshot) => {
            const fetchedCampaigns = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            const campLookup = fetchedCampaigns.reduce((acc, camp) => {
                acc[camp.id] = camp.streamName || `Campaign ${camp.id.slice(-4)}`;
                return acc;
            }, {});
            setCampaigns(fetchedCampaigns);
            setCampaignLookup(campLookup);
        }).catch((err) => {
            setError("Failed to load campaigns.");
        }).finally(() => setLoadingCampaigns(false));
    }, [userId, selectedProjectId]);

    // --- 3. Fetch Monthly Posts (real-time) ---
    useEffect(() => {
        if (!userId || selectedStatuses.length === 0) {
            setMonthlyPosts([]); setLoadingPosts(false); return;
        }
        setLoadingPosts(true); setError('');

        const start = Timestamp.fromDate(startOfMonth(activeStartDate));
        const end = Timestamp.fromDate(endOfMonth(activeStartDate));
        // NOTE: collectionGroup reads across all projects; userId filter applied server-side via security rules. JS filtering by project/campaign is post-fetch.
        const postsRef = collectionGroup(db, "posts");

        const unsubscribes = [];
        // Shared mutable map; each listener updates its slice keyed by query index
        const snapshotData = { 0: new Map(), 1: new Map() };
        let initialLoadCount = 0;
        let expectedListeners = 0;

        const mergeAndSetPosts = () => {
            const combinedPosts = new Map();
            Object.values(snapshotData).forEach(m => {
                m.forEach((val, key) => combinedPosts.set(key, val));
            });
            setMonthlyPosts(Array.from(combinedPosts.values()));
        };

        const statusesForScheduledQuery = selectedStatuses.filter(s => s !== 'posted');

        if (statusesForScheduledQuery.length > 0) {
            expectedListeners++;
            const q1 = query(postsRef, where("userId", "==", userId), where("status", "in", statusesForScheduledQuery), where("scheduledTime", ">=", start), where("scheduledTime", "<=", end));
            const unsub = onSnapshot(q1, (snapshot) => {
                const posts = new Map();
                snapshot.forEach(doc => {
                    const data = doc.data();
                    if (selectedProjectId !== 'all' && data.projectId !== selectedProjectId) return;
                    if (selectedCampaignId !== 'all' && data.streamId !== selectedCampaignId) return;
                    posts.set(doc.id, { id: doc.id, ...data });
                });
                snapshotData[0] = posts;
                initialLoadCount++;
                if (initialLoadCount >= expectedListeners) setLoadingPosts(false);
                mergeAndSetPosts();
            }, (err) => {
                if (err?.code === 'failed-precondition') {
                    setError("Some queries require database indexes.");
                } else {
                    setError("Some posts failed to load.");
                }
                initialLoadCount++;
                if (initialLoadCount >= expectedListeners) setLoadingPosts(false);
            });
            unsubscribes.push(unsub);
        }

        if (selectedStatuses.includes('posted')) {
            expectedListeners++;
            const q2 = query(postsRef, where("userId", "==", userId), where("status", "==", "posted"), where("postedAt", ">=", start), where("postedAt", "<=", end));
            const unsub = onSnapshot(q2, (snapshot) => {
                const posts = new Map();
                snapshot.forEach(doc => {
                    const data = doc.data();
                    if (selectedProjectId !== 'all' && data.projectId !== selectedProjectId) return;
                    if (selectedCampaignId !== 'all' && data.streamId !== selectedCampaignId) return;
                    posts.set(doc.id, { id: doc.id, ...data });
                });
                snapshotData[1] = posts;
                initialLoadCount++;
                if (initialLoadCount >= expectedListeners) setLoadingPosts(false);
                mergeAndSetPosts();
            }, (err) => {
                if (err?.code === 'failed-precondition') {
                    setError("Some queries require database indexes.");
                } else {
                    setError("Some posts failed to load.");
                }
                initialLoadCount++;
                if (initialLoadCount >= expectedListeners) setLoadingPosts(false);
            });
            unsubscribes.push(unsub);
        }

        if (expectedListeners === 0) {
            setLoadingPosts(false);
        }

        return () => unsubscribes.forEach(u => u());
    }, [userId, activeStartDate, selectedProjectId, selectedCampaignId, selectedStatuses]);
    
    // --- Handlers ---
    const handleActiveStartDateChange = ({ activeStartDate }) => setActiveStartDate(activeStartDate);
    const handleProjectChange = (e) => setSelectedProjectId(e.target.value);
    const handleCampaignChange = (e) => setSelectedCampaignId(e.target.value);
    
    const handleStatusChange = (e) => {
        const { value, checked } = e.target;
        setSelectedStatuses(prev => checked ? [...prev, value] : prev.filter(s => s !== value));
    };

    const openModal = useCallback((post) => { setSelectedPostForModal(post); setIsModalOpen(true); }, []);
    const closeModal = useCallback(() => { setIsModalOpen(false); setSelectedPostForModal(null); }, []);

    // Escape key handler for modal
    useEffect(() => {
        if (!isModalOpen) return;
        const handleKeyDown = (e) => { if (e.key === 'Escape') closeModal(); };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isModalOpen, closeModal]);

    const handleCancelPost = useCallback(async () => {
        if (!selectedPostForModal || isCancelling) return;
        const confirmed = await confirm({ title: 'Cancel Post', message: 'Are you sure you want to cancel this scheduled post?', confirmText: 'Cancel Post', cancelText: 'Keep' });
        if (!confirmed) return;
        setIsCancelling(true);
        try {
            await cancelPostFn({ postId: selectedPostForModal.id, projectId: selectedPostForModal.projectId });
            setMonthlyPosts(prev => prev.map(p => p.id === selectedPostForModal.id ? { ...p, status: 'cancelled' } : p));
            addToast("Post successfully cancelled.", 'success');
            closeModal();
        } catch (err) {
            addToast("Cancellation failed. Please try again.", 'error');
        } finally {
            setIsCancelling(false);
        }
    }, [selectedPostForModal, isCancelling, confirm, addToast, closeModal]);

    // --- Computed Data ---
    const postsForSelectedDay = useMemo(() => {
        if (!selectedDate) return [];
        const dayStart = startOfDay(selectedDate);
        return monthlyPosts.filter(post => {
            const displayDate = getCalendarDisplayDate(post);
            return displayDate && isEqual(startOfDay(displayDate), dayStart);
        }).sort((a, b) => (getCalendarDisplayDate(a)?.getTime() || 0) - (getCalendarDisplayDate(b)?.getTime() || 0));
    }, [selectedDate, monthlyPosts]);

    const datesWithPosts = useMemo(() => {
        const dates = new Set();
        monthlyPosts.forEach(post => {
            const displayDate = getCalendarDisplayDate(post);
            if (displayDate) dates.add(getDateString(startOfDay(displayDate)));
        });
        return dates;
    }, [monthlyPosts]);

    const renderTileContent = useCallback(({ date, view }) => {
        if (view === 'month' && datesWithPosts.has(getDateString(startOfDay(date)))) {
            return <div className="h-1.5 w-1.5 bg-slate-blue rounded-full mx-auto mt-1 shadow-[0_0_5px_rgba(212,255,0,0.5)]"></div>;
        }
        return null;
    }, [datesWithPosts]);

    // --- Render ---
    return (
        <div className="space-y-10 max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-10 min-h-[80vh]">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-b border-white/5 pb-8">
                <div>
                     <div className="flex items-center gap-2 mb-2">
                         <span className="px-3 py-1 bg-slate-blue/10 rounded-full border border-slate-blue/20 text-[10px] uppercase font-black tracking-widest text-slate-blue">
                             Schedule
                         </span>
                     </div>
                     <h1 className="text-5xl font-black text-cream uppercase tracking-tighter drop-shadow-lg">
                        Content <span className="text-transparent bg-clip-text bg-gradient-to-r from-slate-blue to-cream pr-2">Calendar</span>
                     </h1>
                     <p className="text-cream/50 mt-2 font-medium text-lg">Visualize and manage your publishing schedule.</p>
                </div>
            </div>

            {/* Filter Section */}
            <div className="bg-warm-gray/10 backdrop-blur-md rounded-[3rem] border border-white/5 shadow-xl p-8 mb-8">
                <div className="flex items-center gap-2 mb-4">
                    <FiFilter className="text-slate-blue" />
                    <span className="text-xs font-bold uppercase tracking-widest text-white/50">View Controls</span>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                        <label htmlFor="project-filter" className="text-xs font-bold text-cream/40 uppercase tracking-widest mb-2 block">Project</label>
                        <select 
                            id="project-filter" 
                            value={selectedProjectId} 
                            onChange={handleProjectChange} 
                            disabled={loadingProjects} 
                            className="block w-full bg-black/20 border border-white/5 rounded-2xl text-cream focus:border-slate-blue focus:ring-1 focus:ring-slate-blue py-4 px-6 appearance-none font-medium hover:bg-black/30 transition-colors cursor-pointer"
                        >
                            <option value="all" className="bg-warm-dark text-cream">All Projects</option>
                            {loadingProjects && <option disabled>Loading...</option>}
                            {!loadingProjects && projects.map(p => ( <option key={p.id} value={p.id} className="bg-warm-dark text-cream">{p.projectName || `Project ${p.id.slice(-4)}`}</option> ))}
                        </select>
                    </div>
                     <div>
                         <label htmlFor="campaign-filter" className="text-xs font-bold text-cream/40 uppercase tracking-widest mb-2 block">Campaign</label>
                         <select 
                            id="campaign-filter" 
                            value={selectedCampaignId} 
                            onChange={handleCampaignChange} 
                            disabled={loadingCampaigns || selectedProjectId === 'all'} 
                            className="block w-full bg-black/20 border border-white/5 rounded-2xl text-cream focus:border-slate-blue focus:ring-1 focus:ring-slate-blue py-4 px-6 appearance-none font-medium hover:bg-black/30 transition-colors cursor-pointer"
                         >
                             <option value="all" className="bg-warm-dark text-cream">All Campaigns</option>
                             {selectedProjectId !== 'all' && loadingCampaigns && <option disabled>Loading...</option>}
                             {selectedProjectId !== 'all' && !loadingCampaigns && campaigns.map(c => ( <option key={c.id} value={c.id} className="bg-warm-dark text-cream">{c.streamName || `Campaign ${c.id.slice(-4)}`}</option> ))}
                         </select>
                     </div>
                    <div>
                        <label className="form-label">Status</label>
                        <div className="flex flex-wrap gap-x-6 gap-y-2 pt-2">
                             {['scheduled', 'posted', 'failed', 'draft', 'cancelled'].map(status => (
                                <label key={status} className="flex items-center cursor-pointer group">
                                    <input 
                                        id={`status-${status}`} 
                                        type="checkbox" 
                                        value={status} 
                                        checked={selectedStatuses.includes(status)} 
                                        onChange={handleStatusChange} 
                                        className="h-4 w-4 bg-warm-gray/30 border-white/5 rounded checked:bg-slate-blue checked:border-slate-blue focus:ring-1 focus:ring-slate-blue/50 transition-all cursor-pointer"
                                    />
                                    <span className="ml-2 text-xs font-bold text-white/60 uppercase tracking-wider group-hover:text-slate-blue transition-colors">{status}</span>
                                 </label>
                             ))}
                         </div>
                    </div>
                </div>
            </div>

             {error && (
                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center gap-3 text-red-400">
                    <FiAlertCircle />
                    <span className="font-medium text-sm">{error}</span>
                </div>
             )}

             {/* Calendar View */}
            <div className="bg-warm-gray/10 backdrop-blur-md rounded-[3rem] border border-white/5 shadow-xl p-10 relative overflow-hidden">
                 {/* Bg Mesh */}
                 <div className="absolute bottom-0 right-0 w-full h-1/2 bg-gradient-to-t from-slate-blue/5 to-transparent pointer-events-none"></div>
               {loadingPosts && (
                   <div className="absolute inset-0 z-50 flex items-center justify-center bg-warm-dark/50 backdrop-blur-sm rounded-[3rem]">
                       <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-slate-blue"></div>
                   </div>
               )}
               <div className="calendar-container">
                    <Calendar
                        onChange={setSelectedDate}
                        value={selectedDate}
                        onActiveStartDateChange={handleActiveStartDateChange}
                        activeStartDate={activeStartDate}
                        tileContent={renderTileContent}
                        className="react-calendar-override"
                        showNeighboringMonth={false}
                        locale="en-GB"
                    />
                </div>
            </div>

             {selectedDate && (
                 <div className="glass-panel-strong p-8 rounded-[3rem] shadow-organic mt-8">
                     <div className="flex justify-between items-center mb-6 border-b border-white/5 pb-6">
                        <h2 className="text-2xl font-black text-white uppercase tracking-tight">
                            Events for <span className="text-slate-blue">{format(selectedDate, 'MMMM d, yyyy')}</span>
                        </h2>
                        <span className="px-3 py-1 bg-white/5 rounded-full border border-white/10 text-xs font-bold text-cream uppercase tracking-widest">
                            {postsForSelectedDay.length} Items
                        </span>
                     </div>
                     
                     <div className="min-h-[200px]">
                        {loadingPosts && postsForSelectedDay.length === 0 ? (
                            <div className="text-center py-10 text-white/30 italic">Checking schedule...</div>
                        ) : !loadingPosts && postsForSelectedDay.length === 0 ? (
                            <div className="text-center py-10 flex flex-col items-center">
                                <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mb-4">
                                    <FiCalendar className="w-6 h-6 text-white/20" />
                                </div>
                                <p className="text-cream/40 font-medium">No events scheduled for this day.</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {postsForSelectedDay.map(post => {
                                    const displayTime = getCalendarDisplayDate(post);
                                    const timeString = displayTime ? format(displayTime, 'HH:mm') : '--:--';
                                    
                                    let StatusIcon = FiClock;
                                    
                                    if (post.status === 'posted') { StatusIcon = FiCheckCircle; }
                                    else if (post.status === 'scheduled') { StatusIcon = FiClock; }
                                    else if (post.status === 'failed') { StatusIcon = FiAlertCircle; }
                                    else if (post.status === 'draft') { StatusIcon = FiEdit3; }
                                    else if (post.status === 'cancelled') { StatusIcon = FiSlash; }

                                    return (
                                        <div
                                            key={post.id}
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => openModal(post)}
                                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openModal(post); } }}
                                            className="group p-5 bg-warm-gray/10 backdrop-blur-md border border-white/5 rounded-[2rem] hover:bg-warm-gray/20 hover:border-slate-blue/30 hover:shadow-glow cursor-pointer transition-all duration-300 flex items-center space-x-5"
                                        >
                                            <div className={`h-12 w-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${post.status === 'scheduled' ? 'bg-slate-blue/10 text-slate-blue' : 'bg-white/5 text-white/40'} border border-white/5 group-hover:scale-110 transition-transform`}>
                                                <StatusIcon className={`w-5 h-5 ${post.status === 'posted' ? 'text-pastel-green' : post.status === 'failed' ? 'text-soft-coral' : ''}`} />
                                            </div>
                                            
                                            <div className="flex-grow min-w-0">
                                                <div className="flex items-center gap-3 mb-1.5">
                                                    <span className="text-xs font-mono font-bold text-slate-blue bg-black/20 px-2 py-1 rounded-lg border border-white/5">{timeString}</span>
                                                    <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-lg border ${
                                                        post.status === 'posted' ? 'bg-pastel-green-bg/10 border-pastel-green/20 text-pastel-green' : 
                                                        post.status === 'failed' ? 'bg-soft-coral/10 border-soft-coral/20 text-soft-coral' :
                                                        'bg-white/5 border-white/10 text-cream/40'
                                                    }`}>
                                                        {post.status}
                                                    </span>
                                                </div>
                                                <p className="text-sm font-medium text-cream group-hover:text-white truncate transition-colors">
                                                    {typeof post.content === 'object' ? (post.content?.facebook || post.content?.instagram || <i>(No content)</i>) : (post.content || <i>(No content)</i>)}
                                                </p>
                                                <p className="text-[10px] text-cream/30 uppercase tracking-widest mt-1 font-bold">
                                                    {projectLookup[post.projectId] || 'Unknown Project'}
                                                </p>
                                            </div>
                                            
                                            <div className="h-10 w-10 rounded-full bg-white/5 flex items-center justify-center text-cream/20 group-hover:bg-slate-blue group-hover:text-warm-dark transition-all duration-300 opacity-0 group-hover:opacity-100 transform translate-x-2 group-hover:translate-x-0">
                                                <FiArrowRight />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                     </div>
                 </div>
             )}

             {isModalOpen && selectedPostForModal && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 backdrop-blur-md animate-fade-in" role="dialog" aria-modal="true" aria-labelledby="calendar-modal-title">
                    <div className="bg-warm-dark border border-white/10 rounded-2xl shadow-2xl p-6 max-w-lg w-full relative animate-fade-in-up">
                        <button onClick={closeModal} className="absolute top-4 right-4 text-white/30 hover:text-white transition-colors">
                            <FiX size={24} />
                        </button>
                        
                        <h3 id="calendar-modal-title" className="text-lg font-black text-white uppercase tracking-tight mb-6 border-b border-white/10 pb-4 flex items-center gap-2">
                            <span className="w-1 h-6 bg-slate-blue rounded-full"></span>
                            Post Details
                        </h3>

                        <div className="space-y-4 text-sm max-h-[60vh] overflow-y-auto custom-scrollbar pr-2">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-warm-gray/10 p-3 rounded-lg border border-white/5">
                                    <p className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Project</p>
                                    <p className="font-bold text-white">{projectLookup[selectedPostForModal.projectId] || 'N/A'}</p>
                                </div>
                                <div className="bg-warm-gray/10 p-3 rounded-lg border border-white/5">
                                     <p className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Campaign</p>
                                     <p className="font-bold text-white">{selectedPostForModal.streamId ? (campaignLookup[selectedPostForModal.streamId] || 'N/A') : 'Single Post'}</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-warm-gray/10 p-3 rounded-lg border border-white/5">
                                    <p className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Platform</p>
                                    <p className="font-bold text-white capitalize">{selectedPostForModal.platform || 'N/A'}</p>
                                </div>
                                <div className="bg-warm-gray/10 p-3 rounded-lg border border-white/5">
                                    <p className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Status</p>
                                    <span className={`inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                                        selectedPostForModal.status === 'posted' ? 'bg-pastel-green-bg/30 text-pastel-green' : 
                                        selectedPostForModal.status === 'scheduled' ? 'bg-slate-blue/20 text-slate-blue' : 
                                        'bg-white/10 text-white/50'
                                    }`}>
                                        {selectedPostForModal.status}
                                    </span>
                                </div>
                            </div>
                            
                            <div className="bg-warm-gray/10 p-3 rounded-lg border border-white/5">
                                <p className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Timing</p>
                                <p className="font-mono text-white text-xs">
                                    {selectedPostForModal.status === 'posted' && selectedPostForModal.postedAt
                                        ? format(selectedPostForModal.postedAt?.toDate ? selectedPostForModal.postedAt.toDate() : new Date(selectedPostForModal.postedAt), 'Pp')
                                        : selectedPostForModal.scheduledTime
                                            ? format(selectedPostForModal.scheduledTime?.toDate ? selectedPostForModal.scheduledTime.toDate() : new Date(selectedPostForModal.scheduledTime), 'Pp')
                                            : 'Pending'}
                                </p>
                            </div>

                            {selectedPostForModal.status === 'failed' && selectedPostForModal.lastAttemptError && (
                                <div className="bg-red-500/10 p-3 rounded-lg border border-red-500/20">
                                    <p className="text-[10px] uppercase tracking-widest text-red-400 mb-1">Error Report</p>
                                    <p className="text-red-300 text-xs font-mono">Publishing failed — check your platform connection.</p>
                                </div>
                            )}

                            <div>
                                <p className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Content Payload</p>
                                <div className="bg-warm-dark/50 p-4 rounded-xl border border-white/5 text-white/80 leading-relaxed font-light text-xs">
                                    <p className="whitespace-pre-wrap">
                                        {typeof selectedPostForModal.content === 'object' 
                                            ? (selectedPostForModal.content?.facebook || selectedPostForModal.content?.instagram || '(No content)')
                                            : (selectedPostForModal.content || '(No content)')}
                                    </p>
                                </div>
                            </div>

                            {selectedPostForModal.status === 'posted' && selectedPostForModal.platformPostId && (() => {
                                const ids = (selectedPostForModal.platformPostId || '').split(',').filter(Boolean);
                                const links = ids.map(entry => {
                                    const [platform, ...rest] = entry.split(':');
                                    const rawValue = rest.join(':');
                                    if (platform === 'facebook') {
                                        return { platform: 'Facebook', href: `https://www.facebook.com/${rawValue}` };
                                    } else if (platform === 'instagram') {
                                        const permalink = rawValue.includes('|') ? rawValue.split('|').slice(1).join('|') : null;
                                        return permalink ? { platform: 'Instagram', href: permalink } : null;
                                    } else if (platform === 'linkedin') {
                                        return { platform: 'LinkedIn', href: `https://www.linkedin.com/feed/update/${rawValue}` };
                                    }
                                    return null;
                                }).filter(Boolean);
                                if (links.length === 0) return null;
                                return (
                                    <div className="pt-2 space-y-2">
                                        {links.map(link => (
                                            <a key={link.platform} href={link.href} target="_blank" rel="noopener noreferrer" className="btn btn-primary w-full py-2 text-xs">
                                                <FiExternalLink className="mr-2" /> View on {link.platform}
                                            </a>
                                        ))}
                                    </div>
                                );
                            })()}
                        </div>

                        <div className="mt-6 pt-4 border-t border-white/5 flex justify-end gap-3">
                            <button onClick={closeModal} className="btn btn-ghost text-xs px-4">Close Info</button>
                            {selectedPostForModal.status === 'scheduled' && (
                                <button 
                                    onClick={handleCancelPost} 
                                    disabled={isCancelling} 
                                    className="btn btn-danger text-xs px-4"
                                >
                                    {isCancelling ? <FiLoader className="animate-spin mr-2" /> : <FiSlash className="mr-2" />}
                                    {isCancelling ? 'Cancelling...' : 'Cancel Post'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
             )}
        </div>
    );
}

export default CalendarScreen;
